import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Button, FormControl, Grid } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUnidadeSelecionada } from "../../../../features/usuario-logado/usuarioLogadoSlice";
import ErrorBadge from "../../../common/components/forms/errorBadge";
import FormHeaderMedium from "../../../common/components/forms/formHeaderMedium";
import SelectStyled from "../../../common/components/input/selectStyled";
import LoadingIndicator from "../../../common/components/loadingIndicator";
import { useUnidadeNegocioGuard } from "../../../common/hooks/useUnidadeNegocioGuard";
import PageLayout from "../../../common/layouts/pageLayout";
import { FollowUpDto } from "../../../common/types/followUp";
import { KEYCLOAK_TOKEN_TIMEOUT } from "../../../../App";
import { setErrorFeedback, setSuccessFeedback } from "../../../../features/feedback/feedbackSlice";
import { fetchByFilterDto } from "../../../../features/follow-up/followUpAPI";
import { downloadXlsxFile, fetchReportXlsx } from "./martinsLog.hooks"; // Reutilizando a função do hook
import MultiSelectStyled, { SimpleMultiSelectOption } from "../../../common/components/input/multiSelectStyled";
import QCXFinalDatePickerField from "../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField";
import { Field, Form } from "react-final-form";
import { fetchClientesWithFilterDto } from "../../../../features/cliente/clienteAPI";

const MartinsLogImportPage = () => {
  useUnidadeNegocioGuard();
  const dispatch = useDispatch();

  const [error, setError] = useState<string | string[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processo, setProcesso] = useState<string | undefined>(undefined);
  const [inicioVigencia, setInicioVigencia] = useState<string | null>(null);
  const [fimVigencia, setFimVigencia] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);

  const [clientes, setClientes] = useState<any[]>([]);
  const [processosList, setProcessosList] = useState<string[]>([]);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState<boolean>(false);

  const { keycloak } = useKeycloak();
  const selectedUnidade: number = useSelector(selectUnidadeSelecionada);
  const { token } = keycloak;

  const url =
    process.env.REACT_APP_QCX_API_BASE_URL! + '/' + process.env.REACT_APP_QCX_API_FATURA_ENDPOINT!
    + "/martins/import-report";

  const clearData = () => {
    setProcesso(undefined);
    setInicioVigencia(null);
    setFimVigencia(null);
    setSelectedClients([]);
    setIsLoading(false);
  };

  const handleClientsChange = (event: SyntheticEvent<Element, Event>, value: SimpleMultiSelectOption[] | null) => {
    setSelectedClients(value || []);
    setProcesso(undefined);
  };

  const handleSubmit = async () => {
    if (token && selectedUnidade !== undefined) {
      keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
      setIsLoading(true);

      let inicio = "";
      let fim = "";

      try {
        if (inicioVigencia) inicio = new Date(inicioVigencia).toISOString().slice(0, 10);
        if (fimVigencia) fim = new Date(fimVigencia).toISOString().slice(0, 10);
      } catch (e) {
        dispatch(setErrorFeedback({ message: "Formato de data inválido." }));
        setIsLoading(false);
        return;
      }

      const clientesStr = selectedClients.map(c => c.id).join(',');

      const queryParams = new URLSearchParams();
      if (processo) queryParams.append("processo", processo);
      if (inicio) queryParams.append("inicio", inicio);
      if (fim) queryParams.append("fim", fim);
      if (clientesStr) queryParams.append("clientes", clientesStr);

      const endpoint = `${url}/gerar-xlsx?${queryParams.toString()}`;
      const data = await fetchReportXlsx(token, selectedUnidade.toString(), endpoint);

      if (data.statusCode === 500 || data.statusCode === 400 || data.statusCode === 404) {
        dispatch(setErrorFeedback({
          message: data.message || "Falha ao gerar relatório Broker File."
        }));
      } else if (data.statusCode === 200 && data.file) {
        const dataAtual = new Date().toISOString().slice(0, 10);
        const fileName = processo ? `Broker File - Martins - ${processo}.xlsx` : `Broker File - Martins - ${dataAtual}.xlsx`;
        
        downloadXlsxFile(data.file, fileName);

        dispatch(setSuccessFeedback({
          message: "Broker File gerado com sucesso!"
        }));

        clearData();
      }

      setIsLoading(false);
    }
  };

  const fetchDadosIniciais = async () => {
    if (!selectedUnidade) return;

    try {
      const { data } = await fetchClientesWithFilterDto([
        { name: 'filterDadosMinimos', value: 'TRUE' },
        { name: 'unidadesDeNegocio.id', value: selectedUnidade.toString() }
      ]);
      setClientes(data);
    } catch (error) {
      dispatch(setErrorFeedback({ message: 'Erro ao obter clientes:' }));
      setClientes([]);
    }
  };

  useEffect(() => {
    const fetchProcessosPorCliente = async () => {
      if (selectedClients.length > 0) {
        setIsFetchingProcesses(true);
        try {
          const clientIds = selectedClients.map(c => c.id).join(',');

          const { data } = await fetchByFilterDto([
            { name: 'filterDadosMinimos', value: 'TRUE' },
            { name: 'tennantUnidadeDeNegocio.id', value: selectedUnidade.toString() },
            { name: 'importador.id', value: clientIds } 
          ]);

          const mapeados = data.map((followUp: FollowUpDto) => followUp.numero);
          const unicos = mapeados.filter((proc: string, index: number) => mapeados.indexOf(proc) === index);

          setProcessosList(unicos);
        } catch (error) {
          dispatch(setErrorFeedback({ message: 'Erro ao carregar processos dos clientes.' }));
          setProcessosList([]);
        } finally {
          setIsFetchingProcesses(false);
        }
      } else {
        setProcessosList([]);
        setProcesso(undefined);
      }
    };

    fetchProcessosPorCliente();
  }, [selectedClients, selectedUnidade]);

  useEffect(() => {
    fetchDadosIniciais();
    setSelectedClients([]);
    setProcesso(undefined);
  }, [selectedUnidade]);

  const isFormValid = processo || inicioVigencia || fimVigencia || selectedClients.length > 0;

  return (
    <PageLayout
      title={"Broker File"}
      icon={<InsertDriveFileIcon color={"secondary"} />}
    >
      {isLoading ? (
        <LoadingIndicator
          message={isLoading ? "Gerando Broker File..." : "Carregando Dados..."}
        />
      ) : (
        <Form onSubmit={() => { }}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <FormHeaderMedium>
                  Selecione as informações para gerar o arquivo
                </FormHeaderMedium>

                {/* Processo */}
                <Grid item sm={6} md={5} lg={3}>
                  <SelectStyled
                    options={processosList.map((proc, index) => ({
                      id: index,
                      value: proc,
                    }))}
                    label={isFetchingProcesses ? "Carregando Processos..." : "Processo"}
                    required={false}
                    disablePortal={selectedClients.length === 0 || isFetchingProcesses}
                    onChangeAction={(event, newValue) => {
                      setProcesso(newValue?.value);
                    }}
                  />
                </Grid>

                {/* Início da Vigência */}
                <Grid item xs={12} sm={4} md={2}>
                  <Field name="inicioVigencia" render={({ input }) => (
                    <QCXFinalDatePickerField
                      required={false}
                      validate={undefined}
                      id="date-picker-inicio"
                      key="date-picker-inicio"
                      label="Data inicial"
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      {...input}
                      onChange={(date: string | null) => {
                        setInicioVigencia(date);
                        input.onChange(date);
                      }}
                    />
                  )} />
                </Grid>

                {/* Fim da Vigência */}
                <Grid item xs={12} sm={4} md={2}>
                  <Field name="fimVigencia" render={({ input }) => (
                    <QCXFinalDatePickerField
                      required={false}
                      validate={undefined}
                      id="date-picker-fim"
                      key="date-picker-fim"
                      label="Data final"
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      {...input}
                      onChange={(date: string | null) => {
                        setFimVigencia(date);
                        input.onChange(date);
                      }}
                    />
                  )} />
                </Grid>

                {/* Clientes */}
                <Grid item sm={12} style={{ margin: '25px 0 0 0' }}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      disablePortal={false}
                      controlledValues={selectedClients}
                      options={(clientes || []).map((client: any) => ({
                        id: client.id ?? '',
                        value: `(${client.pessoa?.cnpj || ''}) ${client.pessoa?.nome || ''}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                <ErrorBadge error={error} />

                {/* Botão Gerar */}
                <Grid item sm={12} textAlign={"right"}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isLoading || !!error}
                  >
                    Gerar
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      )}
    </PageLayout>
  );
};

export default MartinsLogImportPage;